/**
 * Authentication API
 *
 * Handles user authentication, registration, and profile management.
 */

import { api, setTokens, clearTokens } from './client'

/**
 * Register a new user
 *
 * @param {Object} params
 * @param {string} params.email - User email
 * @param {string} params.password - User password (min 8 chars)
 * @param {string} [params.fullName] - Optional full name
 * @returns {Promise<Object>} Login response with tokens and user
 */
export async function register({ email, password, fullName = null }) {
  const response = await api.post(
    '/auth/register',
    {
      email,
      password,
      full_name: fullName,
    },
    { auth: false }
  )

  // Store tokens
  if (response.access_token) {
    setTokens(response.access_token, response.refresh_token)
  }

  return response
}

/**
 * Login with email and password
 *
 * @param {Object} params
 * @param {string} params.email - User email
 * @param {string} params.password - User password
 * @returns {Promise<Object>} Login response with tokens and user
 */
export async function login({ email, password }) {
  const response = await api.post(
    '/auth/login',
    {
      email,
      password,
    },
    { auth: false }
  )

  // Store tokens
  if (response.access_token) {
    setTokens(response.access_token, response.refresh_token)
  }

  return response
}

/**
 * Logout the current user
 *
 * @returns {Promise<Object>} Success message
 */
export async function logout() {
  try {
    const response = await api.post('/auth/logout')
    return response
  } finally {
    // Always clear tokens, even if API call fails
    clearTokens()
  }
}

/**
 * Get current authenticated user
 *
 * @returns {Promise<Object>} User object
 */
export async function getCurrentUser() {
  return api.get('/auth/me')
}

/**
 * Update current user's profile
 *
 * @param {Object} updates
 * @param {string} [updates.fullName] - New full name
 * @param {string} [updates.avatarUrl] - New avatar URL
 * @returns {Promise<Object>} Updated user object
 */
export async function updateProfile({ fullName, avatarUrl }) {
  const params = {}
  if (fullName !== undefined) params.full_name = fullName
  if (avatarUrl !== undefined) params.avatar_url = avatarUrl

  return api.patch('/auth/me', null, { params })
}

/**
 * Refresh access token using refresh token
 *
 * @param {string} refreshToken - Refresh token
 * @returns {Promise<Object>} New tokens
 */
export async function refreshTokens(refreshToken) {
  const response = await api.post(
    '/auth/refresh',
    {
      refresh_token: refreshToken,
    },
    { auth: false }
  )

  // Store new tokens
  if (response.access_token) {
    setTokens(response.access_token, response.refresh_token)
  }

  return response
}
